import { CrewAgent } from "../../types/crew-types";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useState, useRef, useEffect } from "react";

interface AgentDetailModalProps {
  agent: CrewAgent | null;
  isOpen: boolean;
  onClose: () => void;
}

const AgentDetailModal = ({ agent, isOpen, onClose }: AgentDetailModalProps) => {
  const [videoEnded, setVideoEnded] = useState(false);
  const [videoUrl, setVideoUrl] = useState<string>("");
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Get base file name to derive video paths
  const getBaseFileName = (path: string) => {
    const fileName = path.split('/').pop()?.split('.')[0];
    return fileName;
  };
  
  // Construct action video path
  const getActionVideoPath = (imagePath: string) => {
    const baseName = getBaseFileName(imagePath);
    console.log(`Getting video for agent with image path: ${imagePath}, base name: ${baseName}`);
    
    // Map to existing video files based on the actual files in the directory
    // Direct mappings for agents with their own videos:
    const videoMap: Record<string, string> = {
      'topic-research': 'https://assets.pagemind.ai/crew-agents/topic-research-action.mp4',
      'adjacent-topics': 'https://assets.pagemind.ai/crew-agents/adjacent-topics-action.mp4',
      'adjacent-topics-suggestor': 'https://assets.pagemind.ai/crew-agents/adjacent-topics-suggestor-action.mp4',
      'sector-topic-generator': 'https://assets.pagemind.ai/crew-agents/sector-topic-generator-action.mp4',
      'seo-specialist': 'https://assets.pagemind.ai/crew-agents/seo-specialist-action.mp4',
      'site-sector-classifier': 'https://assets.pagemind.ai/crew-agents/site-sector-classifier-action.mp4',
      'handsoff': 'https://assets.pagemind.ai/crew-agents/handsoff-action.mp4',
      'narrator': 'https://assets.pagemind.ai/crew-agents/narrator-action.mp4',
      'suggestions-with-answers': 'https://assets.pagemind.ai/crew-agents/suggestions-with-answers-action.mp4',
      'chatbot': 'https://assets.pagemind.ai/crew-agents/chatbot-action.mp4',
      'voicebot': 'https://assets.pagemind.ai/crew-agents/voicebot-action.mp4',
      'crawler': 'https://assets.pagemind.ai/crew-agents/crawler-action.mp4',
      'customer-intent': 'https://assets.pagemind.ai/crew-agents/customer-intent-action.mp4',
      'page-semantic': 'https://assets.pagemind.ai/crew-agents/page-semantic-action.mp4',
      'webanalytics': 'https://assets.pagemind.ai/crew-agents/webanalytics-action.mp4',
      'intent-checker': 'https://assets.pagemind.ai/crew-agents/intent-checker-action.mp4',
      'benchmark': 'https://assets.pagemind.ai/crew-agents/benchmark-action.mp4',
      'competitors': 'https://assets.pagemind.ai/crew-agents/competitors-action.mp4',
      'counter-narrative': 'https://assets.pagemind.ai/crew-agents/counter-narrative-action.mp4',
      'behavior-analyst': 'https://assets.pagemind.ai/crew-agents/behavior-analyst-action.mp4',
      'content-generator': 'https://assets.pagemind.ai/crew-agents/content-generator-action.mp4',
      'customer-insights': 'https://assets.pagemind.ai/crew-agents/customer-insights-action.mp4',
      'growth-analyst': 'https://assets.pagemind.ai/crew-agents/growth-analyst-action.mp4',
      'kyc-survey': 'https://assets.pagemind.ai/crew-agents/kyc-survey-action.mp4',
      'lead-capture': 'https://assets.pagemind.ai/crew-agents/lead-capture-action.mp4',
      'page-optimizer': 'https://assets.pagemind.ai/crew-agents/page-optimizer-action.mp4',
      'product-comparison': 'https://assets.pagemind.ai/crew-agents/product-comparison-action.mp4',
      'replay-analyst': 'https://assets.pagemind.ai/crew-agents/replay-analyst-action.mp4',
      'sector-assigner': 'https://assets.pagemind.ai/crew-agents/sector-assigner-action.mp4',
      // New agents
      'auto-evaluator': 'https://assets.pagemind.ai/crew-agents/auto-evaluator-action.mp4',
      'insight-hub': 'https://assets.pagemind.ai/crew-agents/insight-hub-action.mp4',
      'intent-based-recommender': 'https://assets.pagemind.ai/crew-agents/intent-based-recommender-action.mp4',
      'keyword-extractor': 'https://assets.pagemind.ai/crew-agents/keyword-extractor-action.mp4',
      'page-role': 'https://assets.pagemind.ai/crew-agents/page-role-action.mp4',
      'session-summarizer': 'https://assets.pagemind.ai/crew-agents/session-summarizer-action.mp4',
      'style-suggestion': 'https://assets.pagemind.ai/crew-agents/style-suggestion-action.mp4',
      'onboarding': 'https://assets.pagemind.ai/crew-agents/onboarding-action.mp4',
      'orchestration': 'https://assets.pagemind.ai/crew-agents/orchestration-action.mp4',
      'customer-segmentation-agent': 'https://assets.pagemind.ai/crew-agents/customer-segmentation-agent-action.mp4',
      'segment-research-agent': 'https://assets.pagemind.ai/crew-agents/segment-research-agent-action.mp4',
      // Analytics agents
      'chatbot-intent-classifier': 'https://assets.pagemind.ai/crew-agents/chatbot-intent-classifier-action.mp4',
      'chatbot-quality-scorer': 'https://assets.pagemind.ai/crew-agents/chatbot-quality-scorer-action.mp4',
      'chatbot-content-gap-detector': 'https://assets.pagemind.ai/crew-agents/chatbot-content-gap-detector-action.mp4',
      'chatbot-conversation-flow-analyzer': 'https://assets.pagemind.ai/crew-agents/chatbot-conversation-flow-analyzer-action.mp4',
      'chatbot-analytics-agent': 'https://assets.pagemind.ai/crew-agents/chatbot-analytics-agent-action.mp4',
      // Fallback mappings
      'adjacent': 'https://assets.pagemind.ai/crew-agents/adjacent-topics-action.mp4',
      'content-recommender': 'https://assets.pagemind.ai/crew-agents/content-generator-action.mp4',
      'adjacent-server': 'https://assets.pagemind.ai/crew-agents/adjacent-topics-suggestor-action.mp4'
    };
    
    const mappedVideo = videoMap[baseName || ''];
    if (mappedVideo) {
      console.log(`Found video mapping: ${baseName} -> ${mappedVideo}`);
      return mappedVideo;
    }
    
    // Log warning for unmapped videos
    console.warn(`No video mapping found for agent with image: ${baseName}, using fallback`);

    // Default fallback
    return 'https://assets.pagemind.ai/crew-agents/topic-research-action.mp4';
  };

  // Reset state and set video URL when modal opens or agent changes
  useEffect(() => {
    if (isOpen && agent) {
      console.log("Modal opened for agent:", agent.name);
      
      // Reset video ended state
      setVideoEnded(false);
      
      // Set the video URL
      const videoPath = getActionVideoPath(agent.imagePath);
      setVideoUrl(videoPath);
      console.log("Video path set to:", videoPath);
      
      // Add a small delay to ensure DOM is ready
      const timer = setTimeout(() => {
        if (videoRef.current) {
          console.log("Attempting to play video:", videoPath);
          videoRef.current.load(); // Force reload of video
          
          // Play after load complete
          videoRef.current.onloadeddata = () => {
            console.log("Video loaded, playing now");
            videoRef.current?.play().catch(error => {
              console.error("Video play failed:", error);
              setVideoEnded(true);
            });
          };
        }
      }, 100);
      
      return () => clearTimeout(timer);
    }
  }, [isOpen, agent]);
  
  // Add error handling for the video element
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement) return;
    
    const errorHandler = () => {
      console.error("Video error event triggered");
      setVideoEnded(true);
    };
    
    videoElement.addEventListener('error', errorHandler);
    
    return () => {
      videoElement.removeEventListener('error', errorHandler);
    };
  }, [videoRef.current]);

  // Handle video ended event
  const handleVideoEnded = () => {
    console.log("Video playback ended");
    setVideoEnded(true);
  };
  
  if (!agent) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl p-0" aria-describedby="agent-detail-description">
        <VisuallyHidden>
          <DialogTitle>{agent.name} Details</DialogTitle>
        </VisuallyHidden>
        <VisuallyHidden>
          <DialogDescription id="agent-detail-description">
            Detailed information about the {agent.name} agent including role, capabilities, and synergies.
          </DialogDescription>
        </VisuallyHidden>
        <div className="grid grid-cols-1 md:grid-cols-2 w-full">
          <div className="bg-gradient-to-br from-primary/5 to-primary/20 flex flex-col items-center justify-center p-8">
            <div className="w-80 h-80 relative">
              {!videoEnded ? (
                <video
                  ref={videoRef}
                  src={videoUrl}
                  className="w-full h-full object-contain"
                  muted
                  playsInline
                  autoPlay
                  onEnded={handleVideoEnded}
                />
              ) : (
                <img
                  src={agent.imagePath}
                  alt={agent.name}
                  className="w-full h-full object-contain animate-pulse"
                />
              )}
            </div>
            <h2 className="text-2xl font-bold mt-4 text-center">
              {agent.personalName && (
                <span className="block text-3xl mb-1">{agent.personalName}</span>
              )}
              <span className={agent.personalName ? "text-lg font-medium text-muted-foreground" : ""}>{agent.name}</span>
            </h2>
          </div>

          <div className="p-8 overflow-y-auto max-h-[80vh]">
            <div className="border-b border-border py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Role</h3>
              <p>{agent.details.role}</p>
            </div>

            <div className="border-b border-border py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Generated Assets</h3>
              <p>{agent.details.generatedAssets}</p>
            </div>

            <div className="border-b border-border py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Synergies with Other Agents</h3>
              <p>{agent.details.synergies}</p>
            </div>

            <div className="border-b border-border py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Care & Maintenance</h3>
              <p>{agent.details.careAndMaintenance}</p>
            </div>

            <div className="border-b border-border py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Potential Evolutions</h3>
              <p>{agent.details.potentialEvolutions}</p>
            </div>

            <div className="py-4">
              <h3 className="text-sm font-semibold uppercase text-muted-foreground mb-1">Price</h3>
              <p className="font-medium text-primary">{agent.details.price}</p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AgentDetailModal;